/**
 * 🎨 UTILITAIRE CENTRALISÉ - ÉMOJIS DYNAMIQUES POUR CATÉGORIES
 * 
 * Ce fichier centralise la logique d'association d'émojis aux catégories
 * pour éviter la duplication de code dans toute l'application.
 * 
 * Utilisé par :
 * - DynamicCategoryFilter.jsx
 * - CategoryDisplay.jsx
 * - Tous autres composants affichant des catégories
 */

/**
 * Associe automatiquement des émojis aux catégories basé sur leur nom
 * @param {string} categoryName - Le nom de la catégorie
 * @returns {string} L'émoji correspondant à la catégorie
 */
export const getCategoryEmoji = (categoryName) => {
  if (!categoryName) return '📦';
  
  const name = categoryName.toLowerCase();
  
  // Mapping d'émojis basé sur les mots-clés
  const emojiMap = {
    // Véhicules et transport
    'véhicule': '🚗', 'voiture': '🚗', 'auto': '🚗', 'entretien': '🔧',
    'camion': '🚛', 'truck': '🚛', 'poids lourd': '🚛',
    'remorque': '🚚', 'trailer': '🚚',
    'moto': '🏍️', 'scooter': '🛵', 'vélo': '🚲',
    
    // Construction et outils
    'engin': '🏗️', 'chantier': '🏗️', 'construction': '🏭', 'bâtiment': '🏢',
    'échafaudage': '🏗️', 'echafaudage': '🏗️',
    'outil': '🔧', 'perceuse': '🔧', 'marteau': '🔨', 'tournevis': '🪛',
    'électrique': '⚡', 'electrique': '⚡',
    'main': '🔨', 'manuel': '🔨',
    'bricolage': '🏠', 'maison': '🏠', 'rénovation': '🏠',
    
    // Machines et équipements
    'machine': '⚙️', 'équipement': '⚙️', 'appareil': '⚙️',
    'agricole': '🚜', 'tracteur': '🚜', 'ferme': '🌾',
    'aspirateur': '🌪️', 'nettoyage': '🧹', 'ménage': '🧽',
    
    // Événements et divertissement
    'événement': '🎉', 'evenement': '🎉', 'fête': '🎊', 'party': '🎉',
    'tente': '⛺', 'chapiteau': '🎪', 'camping': '⛺',
    'sonorisation': '🔊', 'audio': '🔊', 'musique': '🎵', 'son': '🔊',
    'éclairage': '💡', 'lumière': '💡', 'projecteur': '🔦',
    
    // Technologie et électronique
    'jeu': '🎮', 'gaming': '🎮', 'console': '🎮', 'vidéo': '🎮',
    'électronique': '📱', 'electronique': '📱', 'tech': '💻',
    'ordinateur': '💻', 'pc': '💻', 'laptop': '💻',
    'téléphone': '📱', 'smartphone': '📱', 'mobile': '📱',
    
    // Sport et loisirs
    'sport': '⚽', 'fitness': '🏋️', 'musculation': '💪',
    'vélo': '🚲', 'trottinette': '🛴', 'ski': '🎿',
    'piscine': '🏊', 'plongée': '🤿', 'surf': '🏄',
    
    // Jardinage et extérieur
    'jardin': '🌱', 'jardinage': '🌿', 'plante': '🪴',
    'tondeuse': '🌾', 'taille': '✂️', 'sécateur': '✂️',
    
    // Cuisine et maison
    'cuisine': '🍳', 'culinaire': '👨‍🍳', 'four': '🔥',
    'électroménager': '🏠', 'electromenager': '🏠',
    
    // Photo et vidéo
    'photo': '📸', 'photographie': '📷', 'caméra': '📹',
    'vidéo': '🎥', 'film': '🎬',
    
    // Bébé et enfant
    'bébé': '👶', 'bebe': '👶', 'enfant': '🧒', 'jouet': '🧸',
    'poussette': '👶', 'siège auto': '🚗',
    
    // Animaux
    'animal': '🐕', 'chien': '🐕', 'chat': '🐱', 'aquarium': '🐠',
    
    // Mode et beauté
    'mode': '👗', 'vêtement': '👕', 'chaussure': '👟',
    'beauté': '💄', 'coiffure': '💇', 'parfum': '🌸',
    
    // Défaut générique
    'default': '📦'
  };
  
  // Chercher une correspondance dans le nom
  for (const [keyword, emoji] of Object.entries(emojiMap)) {
    if (keyword !== 'default' && name.includes(keyword)) {
      return emoji;
    }
  }
  
  // Si aucune correspondance, utiliser l'émoji par défaut
  return emojiMap.default;
};

/**
 * Obtient la couleur thématique associée à une catégorie
 * Utilise un hash du nom pour garantir une couleur cohérente
 * @param {string} categoryName - Le nom de la catégorie
 * @returns {string} Code couleur hexadécimal
 */
export const getCategoryColor = (categoryName) => {
  if (!categoryName) return '#757575';
  
  const name = categoryName.toLowerCase().trim();
  
  // Couleurs prédéfinies pour les catégories courantes
  const exactColorMap = {
    // Véhicules - bleu
    'véhicules': '#1976d2',
    'voitures': '#1976d2', 
    'motos': '#1976d2',
    'camions': '#1976d2',
    
    // Construction - orange
    'outils de construction': '#f57c00',
    'engins de chantier': '#f57c00',
    'échafaudages': '#f57c00',
    'outils électriques': '#ff9800',
    'outils à main': '#ff6f00',
    
    // Machines - gris foncé
    'machines agricoles': '#424242',
    'aspirateurs & nettoyage': '#616161',
    
    // Événements - rose
    'événements': '#e91e63',
    'tentes & chapiteaux': '#c2185b',
    'sonorisation': '#ad1457',
    'éclairage événementiel': '#880e4f',
    
    // Tech/Gaming - violet
    'jeux & consoles': '#9c27b0',
    'électronique': '#7b1fa2',
    
    // Sport - vert
    'sport & fitness': '#4caf50',
    
    // Jardin - vert clair
    'jardinage': '#8bc34a',
    
    // Photo - marron
    'photo & vidéo': '#795548',
    
    // Bébé - jaune
    'bébé & enfant': '#ffeb3b',
    
    // Animaux - bleu foncé
    'animaux': '#3f51b5'
  };
  
  // Vérifier d'abord les correspondances exactes
  if (exactColorMap[name]) {
    return exactColorMap[name];
  }
  
  // Système de hash pour générer une couleur cohérente basée sur le nom
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    const char = name.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  // Palette de couleurs harmonieuses
  const colorPalette = [
    '#1976d2', // Bleu
    '#f57c00', // Orange
    '#e91e63', // Rose
    '#9c27b0', // Violet
    '#4caf50', // Vert
    '#ff5722', // Rouge-orange
    '#795548', // Marron
    '#607d8b', // Bleu-gris
    '#3f51b5', // Indigo
    '#8bc34a', // Vert clair
    '#ff9800', // Orange vif
    '#673ab7'  // Violet foncé
  ];
  
  // Utiliser le hash pour sélectionner une couleur de façon cohérente
  const index = Math.abs(hash) % colorPalette.length;
  return colorPalette[index];
};

/**
 * Vérifie si une catégorie est populaire/tendance
 * @param {string} categoryName - Le nom de la catégorie
 * @returns {boolean} True si la catégorie est considérée comme populaire
 */
export const isTrendingCategory = (categoryName) => {
  if (!categoryName) return false;
  
  const name = categoryName.toLowerCase();
  const trendingKeywords = [
    'électronique', 'gaming', 'tech', 'sport', 'fitness',
    'véhicule', 'événement', 'photo', 'vidéo'
  ];
  
  return trendingKeywords.some(keyword => name.includes(keyword));
};
